import { CardDali, CardPicasso, Icon } from "@liberetech/design-system";
import Button from "@liberetech/design-system/Button";
import Hero from "@liberetech/design-system/Hero";
import HeroSplit, { imageSizes } from "@liberetech/design-system/HeroSplit";
import Prose from "@liberetech/design-system/Prose";
import Section, { SectionBackground } from "@liberetech/design-system/Section";
import SectionHeader from "@liberetech/design-system/SectionHeader";
import classNames from "classnames";
import { GetServerSideProps } from "next";
import React from "react";

import ImageCDN from "components/ImageCDN/ImageCDN";
import NextLinkButton from "components/NextLinkButton/NextLinkButton";
import { LanguageAlternates, Seo } from "components/Seo/Seo";
import fetchCms from "lib/client/cms";
import { useTranslation } from "lib/i18n/client";
import { TrackingContextPage } from "lib/tracking";
import withDirectChannelCommonProps from "lib/withDirectChannelCommonProps";
import SectionWithOverlappedImages from "pagesComponents/_shared/SectionWithOverlappedImages/SectionWithOverlappedImages";

import styles from "./[slug].module.css";

const Page: React.FC<PageProps> = ({ pageContent: page }) => {
  const {
    featuredImage,
    hero,
    pageBuilder: { pageBuilder },
    seo,
  } = page;

  const { t } = useTranslation();
  return (
    <>
      <Seo
        title={seo.title || page.title}
        titleTemplate={seo.title ? "%s" : undefined}
        description={seo.metaDesc}
        nofollow={seo.metaRobotsNofollow === "nofollow"}
        noindex={seo.metaRobotsNoindex === "noindex"}
      />
      {pageBuilder.length === 0 || !featuredImage ? (
        <Hero
          title={page.title}
          titleInMobile
          breadcrumbs={[
            {
              title: t("common:breadcrumbs.home"),
              path: "/",
            },
          ]}
        />
      ) : (
        <HeroSplit
          image={
            <ImageCDN
              src={featuredImage.node.sourceUrl}
              sizes={imageSizes}
              alt={hero.claim || page.title}
            />
          }
          title={hero.claim || page.title}
          titleAs="h2"
          label={page.title}
          labelAs="h1"
        >
          {hero.description && (
            <Prose>
              <div dangerouslySetInnerHTML={{ __html: hero.description }} />
            </Prose>
          )}
          {hero.actions && (
            <div className={styles.heroActions}>
              {hero.actions.map((action, i) => (
                <Action action={action} key={i} />
              ))}
            </div>
          )}
        </HeroSplit>
      )}
      <article>
        {pageBuilder.map((section, i) => {
          const Component = componentMap[section.__typename];
          if (!Component) {
            return null;
          }

          return (
            <Component
              key={i}
              {...{
                ...section,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                label: (section as any).label || page.title,
                background: i % 2 === 0 ? "default" : "contrast",
                inverted: i % 2 === 0,
              }}
            />
          );
        })}
      </article>
    </>
  );
};

const SectionFeatures: React.FC<
  PageResponseSectionFeatures & PageSectionWithModifiers
> = (section) => (
  <SectionWithOverlappedImages
    id={section.id}
    className={styles.sectionFeatures}
    title={section.title}
    label={section.label}
    description={
      <>
        {section.description && (
          <div dangerouslySetInnerHTML={{ __html: section.description }} />
        )}
        {section.appearance === "inline" && section.features && (
          <SectionFeaturesFeatures {...section} />
        )}
      </>
    }
    background={section.background}
    inverted={section.inverted}
    images={{
      above: section.images.above.sourceUrl,
      below: section.images.below.sourceUrl,
    }}
  >
    {section.appearance === "default" && (
      <div className="grid">
        <SectionFeaturesFeatures {...section} />
      </div>
    )}
  </SectionWithOverlappedImages>
);

const SectionFeaturesFeatures: React.FC<
  PageResponseSectionFeatures & PageSectionWithModifiers
> = (section) => {
  if (!section.features) {
    return null;
  }

  return (
    <div
      className={classNames(styles.features, {
        [styles.featuresInline]: section.appearance === "inline",
      })}
    >
      {section.features.map((feature, i) => (
        <CardDali
          className={styles.feature}
          title={feature.title}
          titleAs="h3"
          icon={
            <Icon type="main">
              <div dangerouslySetInnerHTML={{ __html: feature.icon }} />
            </Icon>
          }
          elevation={1}
          key={i}
        >
          {feature.description && (
            <Prose className={styles.featureDescription}>
              <div dangerouslySetInnerHTML={{ __html: feature.description }} />
            </Prose>
          )}
        </CardDali>
      ))}
    </div>
  );
};

const SectionCards: React.FC<
  PageResponseSectionCards & PageSectionWithModifiers
> = (section) => (
  <Section background={section.background} id={section.id}>
    <SectionHeader
      className="grid"
      centered
      title={section.title}
      label={section.label}
      description={
        section.description && (
          <div dangerouslySetInnerHTML={{ __html: section.description }} />
        )
      }
    />
    <div className={classNames(styles.sectionCardsCards, "grid")}>
      {section.cards.map((card, i) => (
        <CardPicasso
          className={styles.sectionCardsCard}
          key={i}
          title={card.title}
          image={
            <img
              alt=""
              srcSet={card.image.srcSet}
              sizes="(min-width: 1024px) 25vw, 60vw"
              loading="lazy"
            />
          }
          actions={
            card.url &&
            section.ctaText && (
              <NextLinkButton
                className={styles.sectionCardsCardAction}
                href={card.url}
              >
                {section.ctaText}
              </NextLinkButton>
            )
          }
        >
          {card.description && (
            <Prose compact>
              <div dangerouslySetInnerHTML={{ __html: card.description }} />
            </Prose>
          )}
        </CardPicasso>
      ))}
    </div>
  </Section>
);

const SectionCTA: React.FC<
  PageResponseSectionCTA & PageSectionWithModifiers
> = (section) => (
  <Section background={section.background} id={section.id}>
    <div className="grid">
      <SectionHeader
        title={section.title}
        centered
        label={section.label}
        description={
          section.description && (
            <div dangerouslySetInnerHTML={{ __html: section.description }} />
          )
        }
      />
      <div className={styles.ctaActions}>
        {section.actions.map((action, i) => (
          <Action action={action} key={i} />
        ))}
      </div>
    </div>
  </Section>
);

const SectionContent: React.FC<
  PageResponseSectionContent & PageSectionWithModifiers
> = (section) => (
  <Section
    background={section.background}
    className={classNames(styles.sectionContent, "grid")}
  >
    <Prose>
      <div dangerouslySetInnerHTML={{ __html: section.content }} />
    </Prose>
  </Section>
);

const Action: React.FC<{ action: PageResponseAction }> = ({ action }) => {
  const secondary = action.appearance === "secondary";
  if (action.action === "navigate") {
    return (
      <NextLinkButton href={action.url} secondary={secondary}>
        {action.title}
      </NextLinkButton>
    );
  }

  return (
    <Button
      type="button"
      onClick={() => {
        window.dataLayer &&
          window.dataLayer.push({
            event: action.event.name,
            event_category: action.event.category,
            event_label: action.event.label,
          });
      }}
      secondary={secondary}
    >
      {action.title}
    </Button>
  );
};

const componentMap: { [key in PageResponseSectionType]: React.ElementType } = {
  Page_Pagebuilder_PageBuilder_Features: SectionFeatures,
  Page_Pagebuilder_PageBuilder_Cards: SectionCards,
  Page_Pagebuilder_PageBuilder_Cta: SectionCTA,
  Page_Pagebuilder_PageBuilder_Content: SectionContent,
};

export const getServerSideProps: GetServerSideProps =
  withDirectChannelCommonProps<PageProps>(async ({ locale, params, req }) => {
    if (!locale || !params || !params.slug || Array.isArray(params.slug)) {
      return {
        notFound: true,
      };
    }

    const { page } = (await fetchCms(
      `{
        page(id: "/${locale}/${params.slug}", idType: URI) {
          id
          title
          slug
          seo {
            title
            metaDesc
            metaRobotsNofollow
            metaRobotsNoindex
          }
          featuredImage {
            node {
              srcSet
              sourceUrl
            }
          }
          analytics {
            pageType
          }
          translations {
            language {
              slug
            }
            uri
          }
          hero {
            claim
            description
            actions {
              title
              appearance
              action
              event {
                name
                category
                label
              }
              url
            }
          }
          pageBuilder {
            pageBuilder {
              __typename
              ... on Page_Pagebuilder_PageBuilder_Features {
                id
                description
                label
                title
                images {
                  above {
                    sourceUrl
                  }
                  below {
                    sourceUrl
                  }
                }
                features {
                  icon
                  title
                  description
                }
                appearance
              }
              ... on Page_Pagebuilder_PageBuilder_Cards {
                id
                label
                title
                description
                ctaText
                cards {
                  title
                  description
                  image {
                    srcSet
                  }
                  url
                }
              }
              ... on Page_Pagebuilder_PageBuilder_Cta {
                id
                label
                title
                description
                actions {
                  title
                  appearance
                  action
                  event {
                    name
                    category
                    label
                  }
                  url
                }
              }
              ... on Page_Pagebuilder_PageBuilder_Content {
                content
              }
            }
          }
        }
      }
      `,
      req.context.brand.cmsUrl,
    )) as { page: PageResponse };

    if (!page || page.title === null) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageContent: page,
        trackingContext: {
          page_type: page.analytics.pageType,
        },
        languageAlternates: [
          {
            hrefLang: locale,
            href: `/${locale}/${params.slug}`,
          },
          ...page.translations.reduce(
            (prev: LanguageAlternates, translation) => {
              if (!translation.uri || !translation.language.slug) {
                return prev;
              }
              return [
                ...prev,
                {
                  hrefLang: translation.language.slug,
                  href: translation.uri,
                },
              ];
            },
            [],
          ),
        ],
      },
    };
  });

type PageProps = {
  pageContent: PageResponse;
  trackingContext: TrackingContextPage;
};

type PageResponse = {
  title: string;
  featuredImage: {
    node: {
      srcSet: string;
      sourceUrl: string;
    };
  };
  seo: {
    title: string;
    metaDesc: string;
    metaRobotsNofollow: string;
    metaRobotsNoindex: string;
  };
  analytics: {
    pageType: string;
  };
  translations: Array<{
    uri: string;
    language: {
      slug: string;
    };
  }>;
  hero: {
    claim?: string;
    description?: string;
    actions?: Array<PageResponseAction>;
  };
  pageBuilder: {
    pageBuilder: Array<
      {
        __typename: PageResponseSectionType;
      } & (
        | PageResponseSectionFeatures
        | PageResponseSectionCards
        | PageResponseSectionCTA
        | PageResponseSectionContent
      )
    >;
  };
};

type PageResponseSectionType =
  | "Page_Pagebuilder_PageBuilder_Features"
  | "Page_Pagebuilder_PageBuilder_Cards"
  | "Page_Pagebuilder_PageBuilder_Cta"
  | "Page_Pagebuilder_PageBuilder_Content";

type PageResponseSectionFeatures = {
  id?: string;
  title: string;
  label?: string;
  description?: string;
  images: {
    above: {
      sourceUrl: string;
    };
    below: {
      sourceUrl: string;
    };
  };
  features?: Array<{
    icon: string;
    title: string;
    description?: string;
  }>;
  appearance: "default" | "inline";
};

type PageResponseSectionCards = {
  id?: string;
  title: string;
  label?: string;
  description?: string;
  ctaText?: string;
  cards: Array<{
    title: string;
    description?: string;
    image: {
      srcSet: string;
    };
    url?: string;
  }>;
};

type PageResponseSectionCTA = {
  id?: string;
  title: string;
  label?: string;
  description?: string;
  actions: Array<PageResponseAction>;
};

type PageResponseAction = {
  title: string;
  appearance: "primary" | "secondary";
  action: "navigate" | "event";
  url: string;
  event: {
    name: string;
    category?: string;
    label?: string;
  };
};

type PageResponseSectionContent = {
  content: string;
};

type PageSectionWithModifiers = {
  background?: SectionBackground;
  inverted?: boolean;
};

export default Page;
